import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  Code2,
  Plus,
  Search,
} from "lucide-react";
import { api } from "../../lib/api";

interface WidgetRow {
  id: string;
  company_id: string;
  name: string;
  service_ids: string[];
  primary_color: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
  company: { id: string; slug: string; name: string } | null;
}

interface CompanyRow {
  id: string;
  slug: string;
  name: string;
  active: boolean;
}

export function WidgetsList() {
  const [search, setSearch] = useState("");

  const widgetsQ = useQuery({
    queryKey: ["widgets"],
    queryFn: () => api<{ items: WidgetRow[] }>("/master/widgets"),
  });
  const companiesQ = useQuery({
    queryKey: ["companies"],
    queryFn: () => api<{ items: CompanyRow[] }>("/master/companies"),
  });

  // Group widgets by company and surface companies even when they have none —
  // so the master can see at a glance which clients still need a widget.
  const sections = useMemo(() => {
    const widgetsByCompany = new Map<string, WidgetRow[]>();
    for (const w of widgetsQ.data?.items ?? []) {
      const list = widgetsByCompany.get(w.company_id) ?? [];
      list.push(w);
      widgetsByCompany.set(w.company_id, list);
    }

    const companies = (companiesQ.data?.items ?? [])
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name, "de"));

    return companies
      .filter((c) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
          c.name.toLowerCase().includes(q) ||
          c.slug.toLowerCase().includes(q)
        );
      })
      .map((c) => ({
        company: c,
        widgets: widgetsByCompany.get(c.id) ?? [],
      }));
  }, [widgetsQ.data, companiesQ.data, search]);

  const totalWidgets = widgetsQ.data?.items.length ?? 0;
  const isLoading = widgetsQ.isLoading || companiesQ.isLoading;

  return (
    <div>
      <header className="mb-5 flex flex-wrap items-center gap-3">
        <div>
          <h1 className="page-title">Widgets</h1>
          <p className="text-sm text-slate-500">
            {totalWidgets} Embed-Konfigurationen über{" "}
            {companiesQ.data?.items.length ?? 0} Unternehmen
          </p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              placeholder="Unternehmen suchen…"
              className="input w-72 pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Link to="/master/widgets/new" className="btn-primary">
            <Plus className="h-4 w-4" />
            Neues Widget
          </Link>
        </div>
      </header>

      {isLoading ? (
        <p className="text-slate-500">Lädt…</p>
      ) : sections.length === 0 ? (
        <div className="card flex flex-col items-center justify-center py-16 text-center">
          <Building2 className="mb-3 h-10 w-10 text-slate-300" />
          <p className="text-sm text-slate-500">
            {search
              ? `Kein Unternehmen passt zu „${search}".`
              : "Noch keine Unternehmen angelegt."}
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {sections.map(({ company, widgets }) => (
            <CompanySection
              key={company.id}
              company={company}
              widgets={widgets}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CompanySection({
  company,
  widgets,
}: {
  company: CompanyRow;
  widgets: WidgetRow[];
}) {
  return (
    <section>
      <header className="mb-3 flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-xs font-semibold text-slate-700">
          {initials(company.name)}
        </div>
        <div className="min-w-0">
          <Link
            to={`/master/companies/${company.slug}`}
            className="block truncate text-lg font-semibold text-slate-900 hover:text-brand"
          >
            {company.name}
          </Link>
          <p className="text-xs text-slate-500">
            <span className="font-mono">/{company.slug}</span>
            <span className="mx-1.5 text-slate-300">·</span>
            {widgets.length}{" "}
            {widgets.length === 1 ? "Widget" : "Widgets"}
          </p>
        </div>
        <Link
          to={`/master/widgets/new?company=${company.id}`}
          className="btn-secondary btn-sm ml-auto"
        >
          <Plus className="h-3.5 w-3.5" /> Widget hinzufügen
        </Link>
      </header>

      {widgets.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-5 py-6 text-center text-sm text-slate-500">
          Noch kein Widget für dieses Unternehmen.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {widgets.map((w) => (
            <Link
              key={w.id}
              to={`/master/widgets/${w.id}`}
              className="card group transition-shadow hover:shadow-elevated"
            >
              <div className="mb-3 flex items-center justify-between">
                <span
                  className="h-3 w-3 rounded-full"
                  style={{ background: w.primary_color ?? "#1a1a2e" }}
                  aria-hidden
                />
                {!w.active && (
                  <span className="pill bg-slate-100 text-slate-600 ring-1 ring-slate-200">
                    Inaktiv
                  </span>
                )}
              </div>
              <h3 className="text-base font-semibold text-slate-900 group-hover:text-brand">
                {w.name}
              </h3>
              <p className="mt-2 inline-flex items-center gap-1 text-xs text-slate-500">
                <Code2 className="h-3 w-3" />
                {w.service_ids.length === 0
                  ? "Alle Services"
                  : `${w.service_ids.length} ${w.service_ids.length === 1 ? "Service" : "Services"}`}
              </p>
              <p className="mt-1 text-[11px] text-slate-400">
                aktualisiert{" "}
                {new Date(w.updated_at).toLocaleDateString("de-DE")}
              </p>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}
